import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import PageWrapper from "../../../components/PageWrapper";
import ExcelUpload from "../../../api/excelUpload";
import ProjectsAPI from "../../../api/projects";
import { useSiteContext } from "../../../SiteContext";
import {
    ComparisonTask,
    ComparisonIntermediate,
    ProjectTasksComparison,
} from "../../../interfaces/ExcelUploadInterface";
import DollarUtil from "../../../util/DollarUtil";
import {
    UploadCloud,
    FileInput,       // page header tile (sidebar match)
    GitCompare,      // action dock icon
    UserCheck,       // "Has claims" marker
    Eye,
    EyeOff,
    Shield,          // "Kept" pill
    AlertTriangle,
    AlertOctagon,    // blocked-apply marker (Paint)
    Info,
    Check,
    CheckCircle,
    RefreshCw,
    ChevronDown,
    X,
    File,
    Folder,
    Layers,
    List,
    Upload,
    Briefcase,
    Settings2,
    Table2,
    Search,
} from "lucide-react";
import {
    WcdpImportVariantKey,
    WCDP_IMPORT_VARIANTS,
} from "./wcdpImportVariants";
import "./WcdpProjectImport.css";

// ─── Constants ────────────────────────────────────────────────────────────────

/** Mirrors the accept map the old FileDropzone used for these pages. */
const EXCEL_ACCEPT: Record<string, string[]> = {
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
    "application/octet-stream": [],
};

const DASH = "—";

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ─── Types ────────────────────────────────────────────────────────────────────

type PageState = "setup" | "comparing" | "diff" | "error";

type SheetState = "idle" | "loading" | "ready" | "error";

interface PickedProject {
    projectID: number;
    altProjectID: string;
    name: string;
}

/**
 * Snapshot of exactly what was compared. Apply re-posts THIS, never the live
 * setup state — otherwise editing the setup after a compare would write a file
 * or a job the user never previewed.
 */
interface CompareInput {
    file: File;
    sheetName?: string;
    project: PickedProject;
}

interface ToastItem {
    id: number;
    kind: "ok" | "err";
    msg: string;
    out?: boolean;
}

interface RollResult {
    orig: number;
    rem: number;
    neu: number;
    tasks: number;
    changed: boolean;
}

// ─── Rollup math ──────────────────────────────────────────────────────────────
//
// Amounts:
//  originalTaskAmount  — task's original value when it already existed
//  remainingTaskAmount — what is still unclaimed
//  originalAmount      — Task base field (also used as fallback)
//  newAmount           — the incoming value for new/updated tasks

function currentAmt(t: ComparisonTask): number | null {
    if (t.status === "new")     return t.newAmount ?? null;
    if (t.status === "updated") return t.newAmount ?? null;
    if (t.status === "claimed") return t.remainingTaskAmount ?? t.originalAmount;
    if (t.status === "removed") return null;
    return t.originalAmount; // unchanged
}

function roll(node: ComparisonIntermediate): RollResult {
    let orig = 0, rem = 0, neu = 0, tasks = 0, changed = false;
    (node.tasks ?? []).forEach((t) => {
        tasks++;
        if (t.status !== "unchanged") changed = true;
        const o = t.originalTaskAmount != null ? t.originalTaskAmount
                : (t.status === "new" ? 0 : t.originalAmount);
        const r = t.remainingTaskAmount != null ? t.remainingTaskAmount
                : (t.status === "new" ? 0 : t.originalAmount);
        const n = t.status === "removed" ? 0 : (currentAmt(t) ?? 0);
        orig += o; rem += r; neu += n;
    });
    (node.intermediate ?? []).forEach((c) => {
        const s = roll(c);
        orig += s.orig; rem += s.rem; neu += s.neu; tasks += s.tasks;
        if (s.changed || (c.status && c.status !== "unchanged")) changed = true;
    });
    if (node.status && node.status !== "unchanged") changed = true;
    return { orig, rem, neu, tasks, changed };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtAmt(n: number | null | undefined): React.ReactNode {
    if (n == null) return <span className="wimp-dash">{DASH}</span>;
    return DollarUtil.formatDollar(n);
}

function fmtFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** status → CSS modifier (used for pills and row accents) */
function stClass(status: string): string {
    switch (status) {
        case "new":       return "added";
        case "removed":   return "removed";
        case "updated":   return "updated";
        case "unchanged": return "unchanged";
        case "claimed":   return "unchanged"; // fallback — handled specially before reaching this
        default:          return "unchanged";
    }
}

/** status → display label */
function stLabel(status: string): string {
    switch (status) {
        case "new":       return "Added";
        case "removed":   return "Removed";
        case "updated":   return "Updated";
        case "unchanged": return "Unchanged";
        case "claimed":   return "Unchanged";
        default:          return "Unchanged";
    }
}

/** Legend-filter key for a task ("claimed" is its own bucket, unlike stClass). */
function taskFilterKey(t: ComparisonTask): string {
    return t.status === "claimed" ? "claimed" : stClass(t.status);
}

/**
 * A task carrying employee claims: fully-claimed ("Kept") rows, plus kept rows
 * whose Remaining sits below Original because part of it is already claimed.
 * Removed rows are excluded — they surface under the Removed filter with their
 * own "Claims kept" marker.
 */
function taskHasClaims(t: ComparisonTask): boolean {
    if (t.status === "claimed") return true;
    return (
        t.status !== "removed" &&
        t.remainingTaskAmount != null &&
        t.originalTaskAmount  != null &&
        t.remainingTaskAmount < t.originalTaskAmount
    );
}

/** Does this task match a legend filter? "claimed" means has-claims, not status. */
function matchesFilter(t: ComparisonTask, filter: string): boolean {
    return filter === "claimed" ? taskHasClaims(t) : taskFilterKey(t) === filter;
}

/**
 * Node-level filter match. "unsafe" is the warning-banner scenario: removed
 * intermediates the server did not mark safe (claimed work underneath) — the
 * same `safeToRemove !== true` predicate as hasUnsafeRemovals.
 */
function nodeMatchesFilter(n: ComparisonIntermediate, filter: string): boolean {
    if (filter === "unsafe") return n.status === "removed" && n.safeToRemove !== true;
    if (filter === "removed") return n.status === "removed";
    if (filter === "added")   return n.status === "new";
    return false;
}

/** True when a node or anything beneath it matches the active legend filter. */
function subtreeMatches(n: ComparisonIntermediate, filter: string): boolean {
    if (nodeMatchesFilter(n, filter)) return true;
    if ((n.tasks ?? []).some((t) => matchesFilter(t, filter))) return true;
    return (n.intermediate ?? []).some((c) => subtreeMatches(c, filter));
}

/**
 * Keys of every node whose subtree matches the filter (for one-time reveal).
 * Once a node itself matches, its entire branch is revealed (`inMatched`) —
 * mirrors the render rule that a matched node shows all of its contents.
 */
function collectMatchingNodeKeys(
    nodes:     ComparisonIntermediate[],
    prefix:    string,
    filter:    string,
    inMatched: boolean = false,
): string[] {
    const keys: string[] = [];
    nodes.forEach((n, i) => {
        const key = nodeKeyOf(prefix, i, n.name);
        const matched = inMatched || nodeMatchesFilter(n, filter);
        if (matched || subtreeMatches(n, filter)) {
            keys.push(key);
            if (n.intermediate?.length) {
                keys.push(...collectMatchingNodeKeys(n.intermediate, key, filter, matched));
            }
        }
    });
    return keys;
}

/** True when a removed task has partial employee claims remaining. */
function isPartialClaim(t: ComparisonTask): boolean {
    return (
        t.status === "removed" &&
        t.remainingTaskAmount != null &&
        t.originalTaskAmount  != null &&
        t.remainingTaskAmount < t.originalTaskAmount
    );
}

/** Escape server/user text before it goes into a toast (toasts render as HTML). */
function escapeHtml(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/**
 * Node key = path of `<siblingIndex>:<name>` segments. The index matters:
 * WCDP projects can hold several sibling intermediates with the SAME name,
 * and name-only keys would collide (duplicate React keys corrupt the list
 * when filters mount/unmount branches, and duplicates share expansion state).
 */
function nodeKeyOf(prefix: string, index: number, name: string): string {
    return `${prefix}/${index}:${name}`;
}

/** Collect every node key in the tree (depth-first), for Expand/Collapse all. */
function collectAllNodeKeys(
    nodes: ComparisonIntermediate[],
    prefix: string,
): string[] {
    const keys: string[] = [];
    nodes.forEach((n, i) => {
        const key = nodeKeyOf(prefix, i, n.name);
        keys.push(key);
        if (n.intermediate?.length) {
            keys.push(...collectAllNodeKeys(n.intermediate, key));
        }
    });
    return keys;
}

/**
 * Any removed intermediate the server did NOT mark safe to remove.
 *
 * Predicate preserved verbatim from the legacy ProjectTasksDisplayComparison —
 * `safeToRemove !== true` (so a missing flag counts as unsafe, deliberately).
 */
function hasUnsafeRemovals(intermediates?: ComparisonIntermediate[]): boolean {
    if (!intermediates) return false;
    for (const intermediate of intermediates) {
        if (intermediate.status === "removed" && intermediate.safeToRemove !== true) {
            return true;
        }
        if (intermediate.intermediate && hasUnsafeRemovals(intermediate.intermediate)) {
            return true;
        }
    }
    return false;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface WcdpProjectImportProps {
    variant: WcdpImportVariantKey;
}

const WcdpProjectImport: React.FC<WcdpProjectImportProps> = ({ variant: variantKey }) => {
    const variant = WCDP_IMPORT_VARIANTS[variantKey];
    const { translator } = useSiteContext();
    const pageTitle = String(translator.translate(variant.pageKey));
    /** "workbook" for the Excel variants, "PDF" for UPF — used in the dropzone/setup/error copy. */
    const fileNoun = variant.fileNoun ?? "workbook";

    // ── Page state machine ────────────────────────────────────────────────
    const [pageState,    setPageState]    = useState<PageState>("setup");
    const [comparison,   setComparison]   = useState<ProjectTasksComparison | null>(null);
    const [compareInput, setCompareInput] = useState<CompareInput | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // ── Setup state ───────────────────────────────────────────────────────
    const [selectedFile,  setSelectedFile]  = useState<File | null>(null);
    const [sheetState,    setSheetState]    = useState<SheetState>("idle");
    const [sheetNames,    setSheetNames]    = useState<string[]>([]);
    const [sheetError,    setSheetError]    = useState<string | null>(null);
    const [selectedSheet, setSelectedSheet] = useState<string>("");
    const [project,       setProject]       = useState<PickedProject | null>(null);

    // ── Diff UI state ─────────────────────────────────────────────────────
    const [hideUnchanged, setHideUnchanged] = useState(false);
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    // Legend-chip status filter — SINGLE-select: clicking a chip shows only that
    // status (replacing any previous choice); clicking it again clears. While a
    // filter is set it is authoritative (Hide-unchanged is ignored). Selecting a
    // chip re-expands exactly the matching branches; clearing collapses back to
    // the baseline. In between, expand/collapse stays fully user-driven.
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const filterActive = statusFilter !== null;

    const toggleStatusFilter = (k: string) => {
        if (statusFilter === k) {
            setStatusFilter(null);
            setExpandedNodes(new Set());
            return;
        }
        setStatusFilter(k);
        setExpandedNodes(new Set(
            collectMatchingNodeKeys(comparison?.intermediates ?? [], "", k),
        ));
    };

    const clearStatusFilter = () => {
        setStatusFilter(null);
        setExpandedNodes(new Set());
    };
    const [confirmOpen,   setConfirmOpen]   = useState(false);
    const [successOpen,   setSuccessOpen]   = useState(false);
    const [applyLoading,  setApplyLoading]  = useState(false);

    // ── Toasts ────────────────────────────────────────────────────────────
    const [toasts,   setToasts]  = useState<ToastItem[]>([]);
    const toastIdRef = useRef(0);

    const showToast = useCallback((kind: "ok" | "err", msg: string) => {
        const id = ++toastIdRef.current;
        setToasts((prev) => [...prev, { id, kind, msg }]);
        setTimeout(() => {
            setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, out: true } : t)));
            setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 220);
        }, 3600);
    }, []);

    // ══════════════════════════════════════════════════════════════════════
    // WORKSHEET NAMES (Paint only)
    // ══════════════════════════════════════════════════════════════════════

    const sheetSeqRef = useRef(0);

    const loadSheetNames = useCallback(async (file: File) => {
        if (!variant.fetchSheetNames) return;
        const seq = ++sheetSeqRef.current;
        setSheetState("loading");
        setSheetError(null);
        setSheetNames([]);
        setSelectedSheet("");
        try {
            const names = await variant.fetchSheetNames(file);
            if (seq !== sheetSeqRef.current) return;
            const clean = (names ?? []).filter((n) => typeof n === "string" && n.length > 0);
            setSheetNames(clean);
            setSheetState("ready");
            if (clean.length === 1) setSelectedSheet(clean[0]);
        } catch (err: any) {
            if (seq !== sheetSeqRef.current) return;
            setSheetError(typeof err?.message === "string" ? err.message : "Failed to read worksheet names.");
            setSheetState("error");
        }
    }, [variant]);

    // ══════════════════════════════════════════════════════════════════════
    // FILE DROP
    // ══════════════════════════════════════════════════════════════════════

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (!acceptedFiles.length) return;
        const file = acceptedFiles[0];
        setSelectedFile(file);
        // A new file invalidates the sheet list and any comparison already made.
        setSheetState("idle");
        setSheetNames([]);
        setSheetError(null);
        setSelectedSheet("");
        setComparison(null);
        setCompareInput(null);
        setErrorMessage(null);
        setConfirmOpen(false);
        setSuccessOpen(false);
        setPageState("setup");
        if (variant.needsWorksheet) void loadSheetNames(file);
    }, [variant, loadSheetNames]);

    const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
        onDrop,
        accept:   variant.accept ?? EXCEL_ACCEPT,
        multiple: false,
    });

    // ══════════════════════════════════════════════════════════════════════
    // PROJECT TYPEAHEAD (copy-adapted from components/payrollEdit/ProjectPicker)
    // ══════════════════════════════════════════════════════════════════════

    const projectLabel = (p: PickedProject) => `${p.altProjectID} · ${p.name}`;

    const [pkText,      setPkText]      = useState("");
    const [pkOpen,      setPkOpen]      = useState(false);
    const [pkOptions,   setPkOptions]   = useState<PickedProject[]>([]);
    const [pkHighlight, setPkHighlight] = useState(0);
    const [pkSearching, setPkSearching] = useState(false);
    const pkDebounceRef = useRef<number | null>(null);
    const pkSeqRef      = useRef(0);
    const pkRootRef     = useRef<HTMLDivElement>(null);
    const [pkPlace, setPkPlace] = useState<React.CSSProperties>({});

    // The menu must not contribute to the shell scroll container's overflow
    // (.v2-content is a scroll container, and even an absolutely-positioned
    // menu grows its scrollHeight and pops a scrollbar). Position it FIXED
    // against the viewport instead, measured off the input, clamped to the
    // space available and flipped above the input when there's more room there.
    useLayoutEffect(() => {
        if (!pkOpen) return;
        const measure = () => {
            const el = pkRootRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const below = window.innerHeight - rect.bottom - 16;
            const above = rect.top - 16;
            const up = below < 180 && above > below;
            const maxHeight = Math.max(120, Math.min(260, up ? above : below));
            setPkPlace({
                left: rect.left,
                width: rect.width,
                maxHeight,
                ...(up
                    ? { bottom: window.innerHeight - rect.top + 4 }
                    : { top: rect.bottom + 4 }),
            });
        };
        measure();
        // Capture-phase scroll catches the .v2-content scroller, not just window.
        window.addEventListener("scroll", measure, true);
        window.addEventListener("resize", measure);
        return () => {
            window.removeEventListener("scroll", measure, true);
            window.removeEventListener("resize", measure);
        };
    }, [pkOpen, pkOptions.length]);

    // Close on outside click.
    useEffect(() => {
        const onDoc = (ev: MouseEvent) => {
            if (pkRootRef.current && !pkRootRef.current.contains(ev.target as Node)) setPkOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const runProjectSearch = (q: string) => {
        const seq = ++pkSeqRef.current;
        setPkSearching(true);
        ProjectsAPI.typeahead(q, 25)
            .then((rows) => {
                if (seq !== pkSeqRef.current) return;
                setPkOptions(
                    (rows ?? [])
                        // The legacy picker was active-only; keep that.
                        .filter((p) => p.active !== false)
                        .map((p) => ({
                            projectID: p.projectID, altProjectID: p.altProjectID, name: p.name,
                        })),
                );
                setPkHighlight(0);
            })
            .catch(() => {
                if (seq === pkSeqRef.current) setPkOptions([]);
            })
            .finally(() => {
                if (seq === pkSeqRef.current) setPkSearching(false);
            });
    };

    const onProjectInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const q = ev.target.value;
        setPkText(q);
        setPkOpen(true);
        if (pkDebounceRef.current) window.clearTimeout(pkDebounceRef.current);
        if (q.trim().length < 2) {
            setPkOptions([]);
            return;
        }
        pkDebounceRef.current = window.setTimeout(() => runProjectSearch(q.trim()), 250);
    };

    const pickProject = (p: PickedProject | null) => {
        setProject(p);
        setPkText(p ? projectLabel(p) : "");
        setPkOpen(false);
    };

    const onProjectKeyDown = (ev: React.KeyboardEvent) => {
        if (!pkOpen) {
            if (ev.key === "ArrowDown") {
                setPkOpen(true);
                ev.preventDefault();
                ev.stopPropagation();
            }
            return;
        }
        if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
            ev.preventDefault();
            ev.stopPropagation();
            setPkHighlight((h) => {
                const n = pkOptions.length;
                if (n === 0) return 0;
                return ev.key === "ArrowDown" ? Math.min(h + 1, n - 1) : Math.max(h - 1, 0);
            });
        } else if (ev.key === "Enter") {
            ev.preventDefault();
            ev.stopPropagation();
            if (pkOptions.length > 0) pickProject(pkOptions[Math.min(pkHighlight, pkOptions.length - 1)]);
            else setPkOpen(false);
        } else if (ev.key === "Escape") {
            ev.preventDefault();
            ev.stopPropagation();
            setPkOpen(false);
            setPkText(project ? projectLabel(project) : "");
        }
    };

    // ══════════════════════════════════════════════════════════════════════
    // COMPARE / APPLY
    // ══════════════════════════════════════════════════════════════════════

    const canCompare =
        !!selectedFile && !!project && (!variant.needsWorksheet || !!selectedSheet);

    const missingBits: string[] = [];
    if (!selectedFile) missingBits.push("a file");
    if (variant.needsWorksheet && !selectedSheet) missingBits.push("a worksheet");
    if (!project) missingBits.push("a project");

    const runCompare = useCallback(async (input: CompareInput) => {
        setCompareInput(input);
        setComparison(null);
        setErrorMessage(null);
        setConfirmOpen(false);
        setSuccessOpen(false);
        setPageState("comparing");
        try {
            const response = await variant.compare(
                input.file,
                String(input.project.projectID),
                input.sheetName,
            );
            if (!response) {
                setPageState("error");
                return;
            }
            setExpandedNodes(new Set()); // everything collapsed on first view
            setHideUnchanged(false);
            setStatusFilter(null);
            setComparison(response);
            setPageState("diff");
        } catch (err: any) {
            setErrorMessage(typeof err?.message === "string" ? err.message : null);
            setPageState("error");
        }
    }, [variant]);

    const handleCompare = useCallback(() => {
        if (!selectedFile || !project) return;
        if (variant.needsWorksheet && !selectedSheet) return;
        runCompare({
            file:      selectedFile,
            sheetName: variant.needsWorksheet ? selectedSheet : undefined,
            project,
        });
    }, [selectedFile, project, selectedSheet, variant, runCompare]);

    /** Back to setup with file/sheet/project intact; the comparison is discarded. */
    const handleEditSelections = useCallback(() => {
        setComparison(null);
        setCompareInput(null);
        setErrorMessage(null);
        setConfirmOpen(false);
        setSuccessOpen(false);
        setPageState("setup");
    }, []);

    const handleRetry = useCallback(() => {
        if (compareInput) runCompare(compareInput);
        else handleEditSelections();
    }, [compareInput, runCompare, handleEditSelections]);

    /** Full reset after a successful apply. The project is deliberately kept. */
    const handleResetAfterApply = useCallback(() => {
        setSelectedFile(null);
        setSheetState("idle");
        setSheetNames([]);
        setSheetError(null);
        setSelectedSheet("");
        setComparison(null);
        setCompareInput(null);
        setErrorMessage(null);
        setConfirmOpen(false);
        setSuccessOpen(false);
        setHideUnchanged(false);
        setExpandedNodes(new Set());
        setStatusFilter(null);
        setPageState("setup");
    }, []);

    const handleApply = useCallback(async () => {
        if (!compareInput) return;
        setConfirmOpen(false);
        setApplyLoading(true);
        try {
            const result = await ExcelUpload.uploadWcdpApplyChanges(
                compareInput.file,
                String(compareInput.project.projectID),
                variant.applyUploadType,
                compareInput.sheetName,
            );
            if (!result) {
                showToast("err", "Apply failed. Please try again.");
                return;
            }
            setSuccessOpen(true);
        } catch (err: any) {
            const detail = typeof err?.message === "string" ? err.message : "";
            showToast("err", detail ? `<b>Apply failed</b> ${escapeHtml(detail)}` : "Apply failed. Please try again.");
        } finally {
            setApplyLoading(false);
        }
    }, [compareInput, variant, showToast]);

    const handleFinishSuccess = useCallback(() => {
        const name = compareInput?.project.name ?? "";
        setSuccessOpen(false);
        showToast("ok", `<b>Import applied</b>${name ? ` to ${escapeHtml(name)}` : ""}`);
        handleResetAfterApply();
    }, [compareInput, showToast, handleResetAfterApply]);

    // ── Tree expand / collapse ────────────────────────────────────────────
    const handleExpandAll = useCallback((open: boolean) => {
        if (!comparison) return;
        if (open) {
            setExpandedNodes(new Set(collectAllNodeKeys(comparison.intermediates ?? [], "")));
        } else {
            setExpandedNodes(new Set());
        }
    }, [comparison]);

    const toggleNode = (key: string) => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    // ── Unsafe-removal verdict ────────────────────────────────────────────
    const unsafe = useMemo(
        () => hasUnsafeRemovals(comparison?.intermediates),
        [comparison],
    );
    const applyBlocked = unsafe && variant.blockUnsafeRemovals;

    // ═══════════════════════════════════════════════════════════════════════
    // RENDER HELPERS
    // ═══════════════════════════════════════════════════════════════════════

    // ── Stepper ───────────────────────────────────────────────────────────
    const renderStepper = (activeStep: number, isLoading = false) => {
        const labels = ["Select file & project", "Review comparison", "Apply"];
        const descs  = [`${capitalize(fileNoun)} & job`, "What would change", "Write changes"];
        return (
            <div className="wimp-stepper">
                {labels.map((label, i) => {
                    const n      = i + 1;
                    const isDone = n < activeStep;
                    const isOn   = n === activeStep;
                    return (
                        <React.Fragment key={n}>
                            {i > 0 && (
                                <span className={`wimp-step-line${i < activeStep ? " done" : ""}`} />
                            )}
                            <div className={`wimp-step${isDone ? " done" : ""}${isOn ? " on" : ""}`}>
                                <span className="wimp-s-num">
                                    {isDone ? (
                                        <Check size={14} />
                                    ) : (isLoading && n === 1) ? (
                                        <span className="wimp-spin" style={{ width: 14, height: 14 }} />
                                    ) : n}
                                </span>
                                <span className="wimp-s-lab">
                                    <span className="wimp-s-t">{label}</span>
                                    <span className="wimp-s-d">{descs[i]}</span>
                                </span>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // ── Worksheet field (Paint only) ──────────────────────────────────────
    const renderWorksheetField = () => {
        if (!variant.needsWorksheet) return null;
        return (
            <div className="wimp-field">
                <label className="wimp-f-lab" htmlFor="wimp-worksheet">
                    Worksheet<span className="req">*</span>
                </label>
                {sheetState === "loading" && (
                    <div className="wimp-field-loading">
                        <span className="wimp-spin" />
                        Reading worksheets from the workbook…
                    </div>
                )}
                {sheetState === "error" && (
                    <div className="wimp-field-err">
                        <AlertTriangle size={15} />
                        <span>{sheetError}</span>
                        <button
                            type="button"
                            className="wimp-retry-link"
                            onClick={() => selectedFile && loadSheetNames(selectedFile)}
                        >
                            Retry
                        </button>
                    </div>
                )}
                {sheetState === "ready" && sheetNames.length === 0 && (
                    <div className="wimp-field-empty">
                        No matching worksheets found in this workbook. Check that you picked the
                        right file, then drop it again.
                    </div>
                )}
                {sheetState === "ready" && sheetNames.length > 0 && (
                    <>
                        <select
                            id="wimp-worksheet"
                            className="wimp-select"
                            value={selectedSheet}
                            onChange={(e) => setSelectedSheet(e.target.value)}
                        >
                            <option value="">Select a worksheet</option>
                            {sheetNames.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                        <div className="wimp-f-hint">
                            {sheetNames.length} worksheet{sheetNames.length === 1 ? "" : "s"} in this workbook.
                        </div>
                    </>
                )}
            </div>
        );
    };

    // ── Project field ─────────────────────────────────────────────────────
    const renderProjectField = () => (
        <div className="wimp-field">
            <label className="wimp-f-lab" htmlFor="wimp-project">
                Project<span className="req">*</span>
            </label>
            <div className="wimp-picker" ref={pkRootRef}>
                <input
                    id="wimp-project"
                    className="wimp-in"
                    value={pkText}
                    onChange={onProjectInput}
                    onFocus={(ev) => ev.target.select()}
                    onKeyDown={onProjectKeyDown}
                    placeholder="Search projects by job number or name…"
                    autoComplete="off"
                    spellCheck={false}
                />
                {pkOpen && (
                    <div className="wimp-picker-menu" style={pkPlace} role="listbox">
                        {pkOptions.map((p, i) => (
                            <button
                                key={p.projectID}
                                type="button"
                                role="option"
                                aria-selected={project?.projectID === p.projectID}
                                className={`wimp-picker-opt${pkHighlight === i ? " hl" : ""}`}
                                onMouseDown={(ev) => { ev.preventDefault(); pickProject(p); }}
                            >
                                <span className="wimp-mono">{p.altProjectID}</span> {p.name}
                            </button>
                        ))}
                        {pkOptions.length === 0 && (
                            <div className="wimp-picker-empty">
                                {pkSearching
                                    ? "Searching…"
                                    : pkText.trim().length < 2
                                        ? "Type to search projects"
                                        : "No matches"}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {project ? (
                <span className="wimp-picked">
                    <CheckCircle size={13} />
                    Importing into <b>&nbsp;{project.altProjectID}</b>&nbsp;— {project.name}
                </span>
            ) : (
                <div className="wimp-f-hint">
                    <Search size={12} style={{ verticalAlign: "-1px", marginRight: 5 }} />
                    Type at least two characters. Only active projects are listed.
                </div>
            )}
        </div>
    );

    // ── State A: Setup ────────────────────────────────────────────────────
    const renderSetup = () => (
        <>
            {renderStepper(1)}
            {!selectedFile ? (
                <div className="wimp-dz-wrap">
                    <div
                        {...getRootProps({
                            className: `wimp-dropzone${isDragActive ? " drag" : ""}`,
                        })}
                    >
                        <input {...getInputProps()} />
                        <div className="wimp-dz-ic">
                            <UploadCloud size={30} />
                        </div>
                        <h2>{variant.dropTitle}</h2>
                        <div className="wimp-dz-sub">
                            Pick the {fileNoun}{variant.needsWorksheet ? ", its worksheet" : ""} and the
                            project to import into. We compare the file against the project&apos;s
                            existing tasks and show you exactly what would change before anything is
                            applied.
                        </div>
                        <button
                            type="button"
                            className="wimp-dz-btn"
                            onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
                        >
                            <Folder size={16} />
                            Browse files
                        </button>
                    </div>
                    <div className="wimp-dz-hint">
                        <Info size={14} />
                        {variant.fileHint ?? (
                            <>Accepts a single <code>.xls</code>, <code>.xlsx</code> or <code>.xlsm</code> workbook.</>
                        )}
                    </div>
                </div>
            ) : (
                <div className="wimp-setup-wrap">
                    <div
                        {...getRootProps({
                            className: `wimp-dz-compact${isDragActive ? " drag" : ""}`,
                        })}
                    >
                        <input {...getInputProps()} />
                        <span className="wimp-dzc-ic"><File size={17} /></span>
                        <div className="wimp-dzc-main">
                            <div className="wimp-dzc-name">{selectedFile.name}</div>
                            <div className="wimp-dzc-meta">
                                {fmtFileSize(selectedFile.size)} · drop another {fileNoun} to replace it
                            </div>
                        </div>
                        <button
                            type="button"
                            className="wimp-btn sm wimp-dzc-swap"
                            onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
                        >
                            <RefreshCw size={14} />
                            Change file
                        </button>
                    </div>

                    <div className="wimp-setup-card">
                        <div className="wimp-setup-head">
                            <span className="wimp-bh-ic"><Settings2 size={18} /></span>
                            <div>
                                <h3>Import setup</h3>
                                <div className="wimp-setup-sub">
                                    {variant.needsWorksheet
                                        ? `Choose the worksheet and the project this ${fileNoun} belongs to.`
                                        : `Choose the project this ${fileNoun} belongs to.`}
                                </div>
                            </div>
                        </div>
                        <div className="wimp-setup-body">
                            {renderWorksheetField()}
                            {renderProjectField()}
                        </div>
                        <div className="wimp-setup-foot">
                            {!canCompare && (
                                <span className="wimp-need">
                                    <Info size={14} />
                                    Still need {missingBits.join(" and ")}.
                                </span>
                            )}
                            <span className="wimp-setup-spacer" />
                            <button
                                className="wimp-btn primary"
                                type="button"
                                onClick={handleCompare}
                                disabled={!canCompare}
                            >
                                <GitCompare size={16} />
                                Compare
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    // ── State B: Comparing ────────────────────────────────────────────────
    const renderComparing = () => (
        <>
            {renderStepper(1, true)}
            <div className="wimp-state-card">
                <div className="wimp-sc-ic load">
                    <span className="wimp-spin" style={{ width: 30, height: 30, borderWidth: 3 }} />
                </div>
                <h2>Comparing against existing tasks…</h2>
                <p>
                    Reading the {fileNoun} and diffing every task and intermediate against the
                    project as it stands today.
                </p>
                {compareInput && (
                    <div className="wimp-sc-file">
                        <File size={13} />
                        {compareInput.file.name}
                        {compareInput.sheetName ? ` · ${compareInput.sheetName}` : ""}
                    </div>
                )}
                <div className="wimp-load-bar"><span /></div>
            </div>
        </>
    );

    // ── State D: Error ────────────────────────────────────────────────────
    const renderError = () => (
        <>
            {renderStepper(1)}
            <div className="wimp-state-card">
                <div className="wimp-sc-ic err">
                    <AlertTriangle size={30} />
                </div>
                <h2>We couldn&apos;t compare that file</h2>
                <p>
                    {errorMessage
                        ? `The import was rejected before anything changed. Correct the issue below in the ${fileNoun} (or in the project's setup), then try again.`
                        : `The ${fileNoun} couldn't be read against this project, or a required column is missing. Check the file and the selections, then try again.`}
                </p>
                {errorMessage && (
                    <div className="wimp-sc-err-detail">
                        <span className="wimp-sc-err-label">Details</span>
                        <span className="wimp-sc-err-msg">{errorMessage}</span>
                    </div>
                )}
                {compareInput && (
                    <div className="wimp-sc-file">
                        <File size={13} />
                        {compareInput.file.name}
                        {compareInput.sheetName ? ` · ${compareInput.sheetName}` : ""}
                    </div>
                )}
                <div className="wimp-sc-actions">
                    <button className="wimp-btn" type="button" onClick={handleEditSelections}>
                        <Settings2 size={16} />
                        Edit selections
                    </button>
                    <button className="wimp-btn primary" type="button" onClick={handleRetry}>
                        <RefreshCw size={16} />
                        Retry
                    </button>
                </div>
            </div>
        </>
    );

    // ── Task row (diff view) ──────────────────────────────────────────────
    //
    // Claim-protection rendering rules:
    //   status:'claimed'     → fully-claimed, API keeps it; blue "Kept" pill +
    //                          "Has claims" marker, no strikethrough, never hidden.
    //   status:'removed' + remainingTaskAmount < originalTaskAmount
    //                        → partial claim; removed treatment + amber marker.
    //   status:'removed' (remaining == original or undefined)
    //                        → plain removal; red "Removed" pill + strikethrough.
    const renderTaskRow = (
        t: ComparisonTask,
        key: string,
        inMatchedBranch: boolean = false,
    ): React.ReactNode => {
        const isProtected = t.status === "claimed";
        const isPartial   = isPartialClaim(t);

        // Legend filter wins when active; otherwise honor Hide-unchanged
        // (protected tasks are never hidden by Hide-unchanged). Rows inside a
        // branch whose node matched the filter are always shown.
        if (statusFilter !== null) {
            if (!inMatchedBranch && !matchesFilter(t, statusFilter)) return null;
        } else if (hideUnchanged && t.status === "unchanged" && !isProtected) {
            return null;
        }

        const pillEl: React.ReactNode = isProtected ? (
            <span className="wimp-st-pill kept">
                <Shield size={12} />
                Kept
            </span>
        ) : (
            <span className={`wimp-st-pill ${stClass(t.status)}`}>
                <span className="wimp-dot" />
                {stLabel(t.status)}
            </span>
        );

        // Partial claims on kept rows get the same marker as fully-claimed ones,
        // so an Original ≠ Remaining gap is always explained on the row itself.
        const claimMarkEl: React.ReactNode = taskHasClaims(t) ? (
            <span className="wimp-claim-mark">
                <UserCheck size={12} />
                Has claims
            </span>
        ) : null;

        const partialMarkEl: React.ReactNode = isPartial ? (
            <span className="wimp-partial-mark">
                Claims kept · remaining balance zeroed
            </span>
        ) : null;

        let amtEl: React.ReactNode;
        if (isProtected) {
            const keepAmt = t.remainingTaskAmount ?? t.originalAmount;
            amtEl = (
                <div className="wimp-amt3">
                    <span className="wimp-a-col orig">{fmtAmt(null)}</span>
                    <span className="wimp-a-col rem">{fmtAmt(null)}</span>
                    <span className="wimp-a-col new">{fmtAmt(keepAmt)}</span>
                </div>
            );
        } else if (t.status === "updated") {
            amtEl = (
                <div className="wimp-amt3">
                    <span className="wimp-a-col orig">{fmtAmt(t.originalTaskAmount)}</span>
                    <span className="wimp-a-col rem">{fmtAmt(t.remainingTaskAmount)}</span>
                    <span className="wimp-a-col new">{fmtAmt(t.newAmount)}</span>
                </div>
            );
        } else if (t.status === "removed") {
            amtEl = (
                <div className="wimp-amt3">
                    <span className="wimp-a-col orig">{fmtAmt(t.originalTaskAmount)}</span>
                    <span className="wimp-a-col rem">{fmtAmt(t.remainingTaskAmount)}</span>
                    <span className="wimp-a-col new">{fmtAmt(null)}</span>
                </div>
            );
        } else {
            amtEl = (
                <div className="wimp-amt3">
                    <span className="wimp-a-col orig">{fmtAmt(null)}</span>
                    <span className="wimp-a-col rem">{fmtAmt(null)}</span>
                    <span className="wimp-a-col new">{fmtAmt(currentAmt(t))}</span>
                </div>
            );
        }

        const rowCls = [
            "wimp-task-row",
            `st-${isProtected ? "unchanged" : stClass(t.status)}`,
            isProtected ? "protected" : "",
        ].filter(Boolean).join(" ");

        return (
            <div key={key} className={rowCls}>
                <div className="wimp-task-line no-exp">
                    <div className="wimp-t-namewrap">
                        <div className="wimp-t-tags">
                            {pillEl}
                            {claimMarkEl}
                            {partialMarkEl}
                        </div>
                        <div className="wimp-t-name">
                            {t.name}
                            {t.description && (
                                <div className="wimp-t-desc">{t.description}</div>
                            )}
                        </div>
                    </div>
                    <div className="wimp-t-mid">{amtEl}</div>
                </div>
            </div>
        );
    };

    // ── Recursive intermediate node ───────────────────────────────────────
    const renderNode = (
        node:            ComparisonIntermediate,
        depth:           number,
        pathPrefix:      string,
        index:           number,
        inMatchedBranch: boolean = false,
    ): React.ReactNode => {
        const key = nodeKeyOf(pathPrefix, index, node.name);
        // While filtering, hide branches with no matches; a node that itself
        // matches shows its ENTIRE subtree (so e.g. the "Not safe to remove"
        // filter shows what's inside those intermediates). Expansion is always
        // user-controlled (selecting a chip pre-expands matches once).
        const nodeMatched = statusFilter !== null && nodeMatchesFilter(node, statusFilter);
        if (
            statusFilter !== null && !inMatchedBranch && !nodeMatched &&
            !subtreeMatches(node, statusFilter)
        ) return null;
        const branchMatched = inMatchedBranch || nodeMatched;
        const isOpen = expandedNodes.has(key);
        const s      = roll(node);

        const isRemoved = node.status === "removed";
        const isAdded   = node.status === "new";
        // WCDP servers really do remove: an unsafe removal is a red flag, not a
        // "kept" reassurance like the 2020ph piece import.
        const isUnsafeNode = isRemoved && node.safeToRemove !== true;

        const showWarn = s.changed && !isRemoved && !isAdded;

        let statusChipEl: React.ReactNode = null;
        if (isRemoved) {
            statusChipEl = node.safeToRemove === true ? (
                <span className="wimp-safe-chip">Safe to remove</span>
            ) : (
                <span className="wimp-st-pill removed">
                    <span className="wimp-dot" />
                    Removed
                </span>
            );
        } else if (isAdded) {
            statusChipEl = (
                <span className="wimp-st-pill added">
                    <span className="wimp-dot" />
                    Added
                </span>
            );
        }

        const rollupEl: React.ReactNode = depth === 1 ? (
            <div className="wimp-amt3">
                <span className="wimp-a-col orig">{fmtAmt(s.orig)}</span>
                <span className="wimp-a-col rem">{fmtAmt(s.rem)}</span>
                <span className="wimp-a-col new">{fmtAmt(s.neu)}</span>
            </div>
        ) : (
            <>
                <span className="wimp-n-count">
                    <b>{s.tasks}</b> task{s.tasks === 1 ? "" : "s"}
                </span>
                <div className="wimp-amt3">
                    <span className="wimp-a-col new neu">{fmtAmt(s.neu)}</span>
                </div>
            </>
        );

        const nodeCls = [
            "wimp-node",
            isRemoved ? "st-removed" : isAdded ? "st-added" : "",
            isOpen ? "" : "collapsed",
        ].filter(Boolean).join(" ");

        return (
            <div key={key} className={nodeCls} data-depth={depth}>
                <div className="wimp-node-head" onClick={() => toggleNode(key)}>
                    <ChevronDown className="caret" size={16} />
                    <span className="wimp-n-ic"><Layers size={15} /></span>
                    <span className="wimp-n-name">{node.name}</span>
                    {showWarn && (
                        <span className="wimp-n-warn" title="Contains changes">
                            <AlertTriangle size={16} />
                        </span>
                    )}
                    {isUnsafeNode && (
                        <span className="wimp-partial-mark">Not safe to remove</span>
                    )}
                    {statusChipEl}
                    <div className="wimp-n-rollup">{rollupEl}</div>
                </div>
                <div className="wimp-node-kids">
                    {(node.tasks ?? []).map((t, i) =>
                        renderTaskRow(t, `${key}/task/${i}`, branchMatched)
                    )}
                    {(node.intermediate ?? []).map((c, i) =>
                        renderNode(c, depth + 1, key, i, branchMatched)
                    )}
                </div>
            </div>
        );
    };

    // ── Count strip ───────────────────────────────────────────────────────
    const renderCountStrip = (pt: ProjectTasksComparison) => {
        const sum = pt.summary;
        return (
        <div className="wimp-count-strip">
            <div className="wimp-count-cell claimed">
                <div className="wimp-cc-lab"><span className="wimp-dot" />Claimed</div>
                {/* claimedTasksCount (all existing fully-claimed/locked tasks) —
                    the same figure the v1 page showed — NOT summary.claimed,
                    which only counts tasks the uploaded file also contains. */}
                <div className="wimp-cc-val">{pt.claimedTasksCount}</div>
                <div className="wimp-cc-sub">existing claimed tasks</div>
            </div>
            <div className="wimp-count-cell added">
                <div className="wimp-cc-lab"><span className="wimp-dot" />Additions</div>
                <div className="wimp-cc-val">{sum.additions}</div>
                <div className="wimp-cc-sub">new tasks</div>
            </div>
            <div className="wimp-count-cell removed">
                <div className="wimp-cc-lab"><span className="wimp-dot" />Removals</div>
                <div className="wimp-cc-val">{sum.removals}</div>
                <div className="wimp-cc-sub">tasks removed</div>
            </div>
            <div className="wimp-count-cell updated">
                <div className="wimp-cc-lab"><span className="wimp-dot" />Updates</div>
                <div className="wimp-cc-val">{sum.updates}</div>
                <div className="wimp-cc-sub">amount changed</div>
            </div>
            <div className="wimp-count-cell unchanged">
                <div className="wimp-cc-lab"><span className="wimp-dot" />Unchanged</div>
                <div className="wimp-cc-val">{sum.unchanged}</div>
                <div className="wimp-cc-sub">no change</div>
            </div>
            <div className="wimp-count-cell">
                <div className="wimp-cc-lab">Intermediates</div>
                <div className="wimp-cc-inter">
                    <span className="up">+{sum.intermediatesAdded}</span>
                    <span className="dn">−{sum.intermediatesRemoved}</span>
                </div>
                <div className="wimp-cc-sub">added · removed</div>
            </div>
        </div>
        );
    };

    // ── Warnings ──────────────────────────────────────────────────────────
    const renderWarnings = (pt: ProjectTasksComparison) => {
        const hasSafe = (pt.intermediates ?? []).some((n) => n.safeToRemove === true);
        return (
            <>
                {unsafe && variant.blockUnsafeRemovals && (
                    <div className="wimp-danger-banner">
                        <AlertOctagon size={17} />
                        <div>
                            <b>This file can&apos;t be applied.</b> It would remove existing
                            intermediates that are <b>not safe to remove</b> — they still have claimed
                            tasks underneath. Fix the {fileNoun} so those intermediates stay, then
                            compare again.
                        </div>
                        <button
                            type="button"
                            className="wimp-banner-link"
                            onClick={() => toggleStatusFilter("unsafe")}
                        >
                            {statusFilter === "unsafe" ? "Show everything" : "Show which ones"}
                        </button>
                    </div>
                )}
                {unsafe && !variant.blockUnsafeRemovals && (
                    <div className="wimp-warn-banner">
                        <AlertTriangle size={17} />
                        <div>
                            <b>You are removing tasks and intermediates that currently have claimed
                            tasks.</b> Applying this file will remove them anyway. Review the red
                            rows below before you continue.
                        </div>
                        <button
                            type="button"
                            className="wimp-banner-link"
                            onClick={() => toggleStatusFilter("unsafe")}
                        >
                            {statusFilter === "unsafe" ? "Show everything" : "Show which ones"}
                        </button>
                    </div>
                )}
                {hasSafe && (
                    <div className="wimp-preview-line">
                        <Info size={16} />
                        <div>
                            This file will remove existing intermediates that are{" "}
                            <b>safe to remove</b> (no claimed tasks underneath).
                        </div>
                    </div>
                )}
            </>
        );
    };

    // ── Legend (chips double as status filters) ───────────────────────────
    const legendChip = (k: string, label: React.ReactNode) => (
        <button
            type="button"
            className={`wimp-lg-item ${k}${statusFilter === k ? " on" : ""}`}
            aria-pressed={statusFilter === k}
            title="Click to filter"
            onClick={() => toggleStatusFilter(k)}
        >
            {label}
        </button>
    );

    const renderLegend = () => (
        <div className="wimp-legend">
            <span className="wimp-lg-lab">{filterActive ? "Filtering" : "Legend"}</span>
            {legendChip("added",     <><span className="wimp-dot" />Added</>)}
            {legendChip("removed",   <><span className="wimp-dot" />Removed</>)}
            {legendChip("updated",   <><span className="wimp-dot" />Updated</>)}
            {legendChip("unchanged", <><span className="wimp-dot" />Unchanged</>)}
            {legendChip("claimed",   <><UserCheck size={13} />Has claims</>)}
            {unsafe &&
                legendChip("unsafe", <><AlertTriangle size={13} />Not safe to remove</>)}
            {filterActive && (
                <button
                    type="button"
                    className="wimp-lg-clear"
                    onClick={clearStatusFilter}
                >
                    Clear filter
                </button>
            )}
            <span className="wimp-lg-spacer" />
            <button
                type="button"
                className={`wimp-lg-toggle${hideUnchanged ? " on" : ""}`}
                disabled={filterActive}
                title={filterActive ? "Not available while a status filter is active" : undefined}
                onClick={() => setHideUnchanged((v) => !v)}
            >
                {hideUnchanged ? <Eye size={14} /> : <EyeOff size={14} />}
                <span>{hideUnchanged ? "Show unchanged" : "Hide unchanged"}</span>
            </button>
        </div>
    );

    // ── Tasks & Piece Work card ───────────────────────────────────────────
    const renderTasksCard = (pt: ProjectTasksComparison) => {
        const { summary: sum } = pt;
        const totalTasks = sum.additions + sum.removals + sum.updates + sum.unchanged + sum.claimed;
        return (
            <div className="wimp-bd-card">
                <div className="wimp-bd-head static">
                    <span className="wimp-bh-ic"><List size={18} /></span>
                    <div>
                        <h3>Tasks &amp; Piece Work</h3>
                        <div className="wimp-bd-sub">Lots &amp; intermediates — expand to inspect</div>
                    </div>
                    <div className="wimp-bh-right">
                        <button
                            className="wimp-btn sm"
                            type="button"
                            onClick={() => handleExpandAll(true)}
                        >
                            Expand all
                        </button>
                        <button
                            className="wimp-btn sm"
                            type="button"
                            onClick={() => handleExpandAll(false)}
                        >
                            Collapse all
                        </button>
                    </div>
                </div>
                <div className="wimp-bd-body">
                    <div className="wimp-sec-head">
                        <span className="wimp-sh-t">Tasks</span>
                        <span className="wimp-sh-count">{totalTasks}</span>
                        <div className="wimp-sh-amts">
                            <span className="c">Original</span>
                            <span className="c">Remaining</span>
                            <span className="c">New</span>
                        </div>
                    </div>
                    <div className="wimp-tree">
                        {(pt.tasks ?? []).map((t, i) =>
                            renderTaskRow(t, `top/task/${i}`)
                        )}
                        {(pt.intermediates ?? []).map((n, i) =>
                            renderNode(n, 1, "", i)
                        )}
                        {statusFilter !== null &&
                            !(pt.tasks ?? []).some((t) => matchesFilter(t, statusFilter)) &&
                            !(pt.intermediates ?? []).some((n) => subtreeMatches(n, statusFilter)) && (
                            <div className="wimp-tree-empty">
                                No rows match this filter.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ── Sticky action dock ────────────────────────────────────────────────
    const renderActionDock = (sum: ProjectTasksComparison["summary"]) => (
        <div className="wimp-action-dock">
            <div className="wimp-ad-summary">
                <GitCompare size={17} />
                <span>Applying will</span>
                <div className="wimp-ad-chips">
                    <span className="wimp-ad-chip added">
                        <span className="wimp-dot" />+{sum.additions} add
                    </span>
                    <span className="wimp-ad-chip removed">
                        <span className="wimp-dot" />−{sum.removals} remove
                    </span>
                    <span className="wimp-ad-chip updated">
                        <span className="wimp-dot" />~{sum.updates} update
                    </span>
                </div>
            </div>
            <div className="wimp-ad-spacer" />
            {applyBlocked && (
                <span className="wimp-ad-block">
                    <AlertOctagon size={15} />
                    Blocked — unsafe intermediate removals
                </span>
            )}
            <button className="wimp-btn" type="button" onClick={handleEditSelections}>
                <Settings2 size={16} />
                Edit selections
            </button>
            <button
                className="wimp-btn primary"
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={applyBlocked}
            >
                <Upload size={16} />
                Upload &amp; apply
            </button>
        </div>
    );

    // ── State C: Diff view ────────────────────────────────────────────────
    const renderDiff = () => {
        if (!comparison || !compareInput) return null;
        const pt  = comparison;
        const sum = pt.summary;
        const p   = compareInput.project;
        return (
            <>
                {renderStepper(2)}

                {/* Preview banner */}
                <div className="wimp-preview-banner">
                    <span className="wimp-pb-ic"><Eye size={17} /></span>
                    <div className="wimp-pb-main">
                        <div className="wimp-pb-t">
                            Preview only — nothing has been applied yet
                        </div>
                        <div className="wimp-pb-s">
                            Review the comparison below, then confirm with Upload to write these
                            changes.
                        </div>
                    </div>
                    <span className="wimp-pb-file">
                        <File size={13} />
                        {compareInput.file.name}
                    </span>
                </div>

                {/* Job identity header — from the project the user picked; the WCDP
                    comparison response carries no project name of its own. */}
                <div className="wimp-job-head">
                    <span className="wimp-jh-ic"><Briefcase size={22} /></span>
                    <div className="wimp-jh-main">
                        <div className="wimp-jh-id">
                            <span className="wimp-code">Job {p.altProjectID}</span>
                        </div>
                        <h2 className="wimp-h2">{p.name}</h2>
                    </div>
                    {compareInput.sheetName && (
                        <span className="wimp-jh-file">
                            <Table2 size={13} />
                            {compareInput.file.name} · {compareInput.sheetName}
                        </span>
                    )}
                    <span className="wimp-jh-tag">
                        <CheckCircle size={12} />
                        {pt.hasExistingTasks ? "Existing tasks compared" : "No existing tasks"}
                    </span>
                </div>

                {/* Count strip */}
                {renderCountStrip(pt)}

                {/* Warnings */}
                {renderWarnings(pt)}

                {/* Legend */}
                {renderLegend()}

                {/* Tasks & Piece Work card */}
                {renderTasksCard(pt)}

                {/* Sticky action dock */}
                {renderActionDock(sum)}
            </>
        );
    };

    // ── State E: Confirm modal ────────────────────────────────────────────
    const renderConfirmModal = () => {
        if (!comparison || !compareInput) return null;
        const sum     = comparison.summary;
        const claimed = comparison.claimedTasksCount;
        return (
            <div
                className={`wimp-modal-bg${confirmOpen ? " open" : ""}`}
                onClick={() => setConfirmOpen(false)}
            >
                <div className="wimp-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="wimp-modal-head">
                        <span className="wimp-mi"><Upload size={21} /></span>
                        <div>
                            <h3>Apply changes?</h3>
                            <div className="wimp-modal-ms">
                                These changes will be written to the project immediately.
                            </div>
                        </div>
                        <button
                            className="wimp-modal-x"
                            type="button"
                            onClick={() => setConfirmOpen(false)}
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <div className="wimp-modal-body">
                        <div className="wimp-confirm-target">
                            <span className="t">Importing into</span>
                            <span className="v">{compareInput.project.name}</span>
                            <span className="s">
                                Job {compareInput.project.altProjectID} · {compareInput.file.name}
                                {compareInput.sheetName ? ` · ${compareInput.sheetName}` : ""}
                            </span>
                        </div>
                        <div className="wimp-confirm-counts">
                            <div className="wimp-cc-stat added">
                                <div className="v">{sum.additions}</div>
                                <div className="l">Added</div>
                            </div>
                            <div className="wimp-cc-stat removed">
                                <div className="v">{sum.removals}</div>
                                <div className="l">Removed</div>
                            </div>
                            <div className="wimp-cc-stat updated">
                                <div className="v">{sum.updates}</div>
                                <div className="l">Updated</div>
                            </div>
                        </div>
                        {unsafe && !variant.blockUnsafeRemovals && (
                            <div className="wimp-confirm-note amber">
                                <AlertTriangle size={16} />
                                <span>
                                    You are removing tasks and intermediates that currently have{" "}
                                    <b>claimed tasks</b>. They will be removed anyway.
                                </span>
                            </div>
                        )}
                        <div className="wimp-confirm-note">
                            <Info size={16} />
                            <span>
                                {claimed > 0
                                    ? `This project has ${claimed} claimed task${claimed !== 1 ? "s" : ""}. Applying writes these changes immediately.`
                                    : "Applying writes these changes to the project immediately."}
                            </span>
                        </div>
                    </div>
                    <div className="wimp-modal-foot">
                        <button
                            className="wimp-btn"
                            type="button"
                            onClick={() => setConfirmOpen(false)}
                        >
                            Cancel
                        </button>
                        <button
                            className="wimp-btn primary"
                            type="button"
                            onClick={handleApply}
                            disabled={applyLoading}
                        >
                            {applyLoading ? (
                                <span className="wimp-spin" style={{ width: 15, height: 15 }} />
                            ) : (
                                <Upload size={16} />
                            )}
                            {applyLoading ? "Applying…" : "Apply changes"}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ── State F: Success modal ────────────────────────────────────────────
    const renderSuccessModal = () => {
        if (!comparison) return null;
        const sum = comparison.summary;
        return (
            <div className={`wimp-modal-bg${successOpen ? " open" : ""}`}>
                <div className="wimp-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="wimp-modal-head">
                        <span className="wimp-mi success"><CheckCircle size={21} /></span>
                        <div>
                            <h3>Import applied</h3>
                            <div className="wimp-modal-ms">
                                Changes have been written to the project.
                            </div>
                        </div>
                    </div>
                    <div className="wimp-modal-body">
                        <div className="wimp-confirm-counts">
                            <div className="wimp-cc-stat added">
                                <div className="v">{sum.additions}</div>
                                <div className="l">Added</div>
                            </div>
                            <div className="wimp-cc-stat removed">
                                <div className="v">{sum.removals}</div>
                                <div className="l">Removed</div>
                            </div>
                            <div className="wimp-cc-stat updated">
                                <div className="v">{sum.updates}</div>
                                <div className="l">Updated</div>
                            </div>
                        </div>
                    </div>
                    <div className="wimp-modal-foot">
                        <button
                            className="wimp-btn primary"
                            type="button"
                            onClick={handleFinishSuccess}
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Toasts ────────────────────────────────────────────────────────────
    const renderToasts = () => (
        <div className="wimp-toast-wrap">
            {toasts.map((t) => (
                <div
                    key={t.id}
                    className={`wimp-toast ${t.kind}${t.out ? " out" : ""}`}
                >
                    {t.kind === "ok"
                        ? <CheckCircle size={18} />
                        : <AlertTriangle size={18} />}
                    {/* msg may contain <b> for emphasis — server text is escaped first */}
                    <span
                        className="wimp-t-msg"
                        dangerouslySetInnerHTML={{ __html: t.msg }}
                    />
                </div>
            ))}
        </div>
    );

    // ── Root render ───────────────────────────────────────────────────────
    const renderPage = () => {
        switch (pageState) {
            case "setup":     return renderSetup();
            case "comparing": return renderComparing();
            case "diff":      return renderDiff();
            case "error":     return renderError();
            default:          return renderSetup();
        }
    };

    return (
        <PageWrapper title={pageTitle} backgroundColor="#f4f5f7">
            <div className="wimp">
                <div className="wimp-page">
                    {/* Page header strip — always visible */}
                    <div className="wimp-imp-head">
                        <span className="wimp-ih-logo">
                            <FileInput size={23} />
                        </span>
                        <div className="wimp-ih-main">
                            <h1 className="wimp-ih-title">{pageTitle}</h1>
                            <div className="wimp-ih-sub">{variant.subtitle}</div>
                        </div>
                    </div>
                    {renderPage()}
                </div>
                {renderConfirmModal()}
                {renderSuccessModal()}
                {renderToasts()}
            </div>
        </PageWrapper>
    );
};

export default WcdpProjectImport;
